import createStore from '@skyscanner-internal/save-to-list-client';
import SaveToListModalError from '@skyscanner-internal/save-to-list-client/errors/ModalError';
import deleteItem from '@skyscanner-internal/save-to-list-client/store/items/deleteItem';
import getSavedId from '@skyscanner-internal/save-to-list-client/store/items/getSavedId';
import saveItem from '@skyscanner-internal/save-to-list-client/store/items/saveItem';
import observer from 'saddlebag-observer';
import { v4 as uuid } from 'uuid';

import type { Configs } from '@shared/types/configs';
import type { OnSavedStatusChanged } from '@shared/types/frontend/save-to-list';
import type { I18nService } from '@shared/types/i18n';
import type { EntityId, Stay } from '@shared/types/utils';

import { SAVED_STATUS } from '@shared/constants/save-status';
import logger from '@shared/utils-frontend/logger';
import {
  createCulture,
  createHotelContext,
  createHotelSaveItemRequest,
  createHotelStay,
} from '@shared/utils-frontend/save-to-list';

// When the user clicks the save button, the corresponding `hotelId` will be stored in this global variable so that it can be obtained after logging in.
// Because for non-logged-in users, clicking the save button will trigger an asynchronous login/registration flow, and after the user completes the login, we will not be able to directly obtain the `hotelId user wanted to save in the `sid-login-success` subscription callback.
let selectedHotelIdBeforeLogging: string = '';
let actionIdBeforeLogging: string = '';

const generateActionId = () => {
  const prefix = 'web1';
  const identifier = uuid().toUpperCase();

  return `${prefix}${identifier}`;
};

type InitSaveToListStore = {
  i18n: I18nService;
  configs: Configs;
};

const initStore = async ({
  configs: { saveToListV1BaseEndpoint, saveToListV2BaseEndpoint },
  i18n: {
    culture: { currency, locale, market },
  },
}: InitSaveToListStore) => {
  try {
    await createStore('Hotels-website', locale, market, currency, {
      saveToListV1BaseEndpoint,
      saveToListV2BaseEndpoint,
    });
  } catch (error) {
    logger.error(`Failed to init "save-to-list-client"`, {}, error);
  }
};

type CheckSavedHotels = {
  i18n: I18nService;
  id: string;
  stay: Stay;
  destinationEntityId?: string;
};
const isSavedHotel = ({ destinationEntityId, id, stay }: CheckSavedHotels) => {
  const savedId = getSavedId({
    hotelStay: {
      hotelId: id,
      hotelContext: createHotelContext(stay, destinationEntityId),
    },
  });

  return !!savedId;
};

const isSelectedHotel = (id: string) => id === selectedHotelIdBeforeLogging;

type SaveHotel = {
  i18n: I18nService;
  stay: Stay;
  id: EntityId;
  price?: number;
  onSavedStatusChanged: OnSavedStatusChanged;
  actionId: string;
  destinationEntityId?: string;
};
const saveHotel = async ({
  actionId,
  destinationEntityId,
  i18n,
  id,
  onSavedStatusChanged,
  stay,
}: SaveHotel) => {
  const { currency, locale, market } = i18n.culture;

  const hotelStay = createHotelStay(id, stay, destinationEntityId);
  const cultureSettings = createCulture({ locale, market, currency });

  try {
    const { statusText } = await saveItem(
      createHotelSaveItemRequest(hotelStay, cultureSettings, actionId),
    );
    return statusText;
  } catch (err: any) {
    if (err instanceof SaveToListModalError) {
      onSavedStatusChanged({
        payload: {
          headingText: err.headingText,
          subHeadingText: err.subHeadingText,
        },
        saveStatus: SAVED_STATUS.RESET,
      });
    } else {
      onSavedStatusChanged({
        payload: {},
        saveStatus: SAVED_STATUS.ERROR,
      });
    }
    logger.warn(`Library "save-to-list-client" failed to save hotel ${id}`, {
      err,
    });

    return null;
  }
};

type RemoveHotel = {
  i18n: I18nService;
  stay: Stay;
  id: EntityId;
  onSavedStatusChanged: OnSavedStatusChanged;
  actionId: string;
  destinationEntityId?: string;
};
const removeHotel = async ({
  actionId,
  destinationEntityId,
  i18n,
  id,
  onSavedStatusChanged,
  stay,
}: RemoveHotel) => {
  const { currency, locale, market } = i18n.culture;

  const cultureSettings = createCulture({ locale, market, currency });
  const savedId = getSavedId({
    hotelStay: {
      hotelId: id,
      hotelContext: createHotelContext(stay, destinationEntityId),
    },
  });

  try {
    const { statusMessage } = await deleteItem({
      cultureSettings,
      savedId,
      actionId,
    });
    return statusMessage;
  } catch (err: any) {
    logger.warn(`Library "save-to-list-client" failed to unsaved hotel ${id}`, {
      err,
    });
    onSavedStatusChanged({
      payload: {},
      saveStatus: SAVED_STATUS.ERROR,
    });

    return null;
  }
};

type PushLoginWhileSave = {
  i18n: I18nService;
  id: EntityId;
  actionId: string;
};

const pushLoginWhileSave = async ({
  actionId,
  i18n: {
    culture: { locale, market },
  },
  id,
}: PushLoginWhileSave) => {
  try {
    selectedHotelIdBeforeLogging = id;
    actionIdBeforeLogging = actionId;
    observer.publish('sid-login-button-clicked', {
      actionId,
      loginModalVersion: 'x.x.x',
      locale,
      market,
      authConsumer: 'hotels-website',
      copyType: 'S2L_SAVE',
    });
  } catch (err) {
    logger.error(
      'Failed to retrieve login_form OC component when save hotels',
      {},
      err,
    );
  }
};

const getActionIdBeforeLogging = () => actionIdBeforeLogging;

export {
  initStore,
  isSavedHotel,
  saveHotel,
  removeHotel,
  pushLoginWhileSave,
  isSelectedHotel,
  generateActionId,
  getActionIdBeforeLogging,
};
